import classNames from 'classnames';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import Link from 'next/link';

import { ProjectStateStatusEnum, SearchResponse } from '@/app/api';
import { rubik } from '@/app/utils/fonts';
import { useToast } from '@/app/hooks/useToast';
import { searchApiForClient } from '@/app/utils/apiForClient';

import SearchBar from '@/app/components/SearchBar/SearchBar';
import SearchResultProjectCard from '@/app/components/SearchResultProjectCard/SearchResultProjectCard';

interface SearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_SEARCH_RESULTS_LIMIT = 10;

const Search: FunctionComponent<SearchProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(null);
  const { showGenericErrorToast } = useToast();

  const isProjectsSectionVisible = useMemo(() => !!searchResponse?.projects, [searchResponse]);
  const isCategoriesSectionVisible = useMemo(() => !!searchResponse?.categories, [searchResponse]);
  const isOrganizationsSectionVisible = useMemo(() => !!searchResponse?.organizations, [searchResponse]);

  const isResultsBlockVisible = useMemo(
    () => searchResponse && (isProjectsSectionVisible || isCategoriesSectionVisible || isOrganizationsSectionVisible),
    [searchResponse, isOrganizationsSectionVisible, isCategoriesSectionVisible, isProjectsSectionVisible],
  );

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      if (searchTerm.length < 2) {
        setSearchResponse(null);
        return;
      }

      try {
        const searchResults = await searchApiForClient.getSearch({
          fulltext: searchTerm,
          limit: DEFAULT_SEARCH_RESULTS_LIMIT,
        });

        setSearchResponse(searchResults);
      } catch (error: unknown) {
        showGenericErrorToast();
      }
    },
    [showGenericErrorToast],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchResponse(null);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchResponse) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [searchResponse]);

  useEffect(() => {
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  useDebounce(
    () => {
      handleSearch(searchTerm);
    },
    250,
    [searchTerm],
  );

  return (
    <div
      className={classNames('z-50 transition-all', {
        'absolute w-full h-[64px] lg:h-[96px] left-0 top-[-96px]': !isOpen,
        'absolute w-full h-[64px] lg:h-[96px] left-0 top-0': !isResultsBlockVisible && isOpen,
        'fixed w-full top-0 bottom-0 left-0 bg-secondary-light': isResultsBlockVisible && isOpen,
      })}
    >
      <SearchBar
        searchTerm={searchTerm}
        onChange={setSearchTerm}
        isOpen={isOpen}
        onClose={onClose}
        onSearch={handleSearch}
      />
      <div className="relative top-[64px] lg:top-[96px] max-h-[calc(100%-96px)] overflow-y-auto">
        <div className="py-[24px] px-[24px] my-0 mx-auto w-full default-page-max-width">
          {isProjectsSectionVisible && (
            <div className="max-w-[554px]">
              {!!searchResponse?.projects.length && (
                <div className="mb-[24px]">
                  <h2
                    className={classNames(
                      'text-[12px] tracking-[3px] lg:tracking-[4.5px] font-medium leading-[24px] text-balance uppercase mb-[10px]',
                      rubik.className,
                    )}
                  >
                    {t('components.Search.projectsSubheading')}
                  </h2>
                  {searchResponse?.projects.map((project) => (
                    <SearchResultProjectCard
                      key={`project-${project.id}`}
                      title={project.content?.currentName ?? ''}
                      image={project.content.mainPhoto ?? undefined}
                      href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${project.slug}`}
                      currentAmountInCents={project.statistics.currentAmountInCents}
                      targetAmountInCents={project.statistics.targetAmountInCents ?? undefined}
                      onNavigate={onClose}
                      isProjectActive={
                        !(
                          [ProjectStateStatusEnum.Finished, ProjectStateStatusEnum.Closed] as ProjectStateStatusEnum[]
                        ).includes(project.state.status)
                      }
                      className="mb-[16px]"
                    />
                  ))}
                </div>
              )}
              {!!searchResponse?.categories.length && (
                <div className="mb-[24px]">
                  <h2
                    className={classNames(
                      'text-[12px] tracking-[3px] lg:tracking-[4.5px] font-medium leading-[24px] text-balance uppercase mb-[10px]',
                      rubik.className,
                    )}
                  >
                    {t('components.Search.categoriesSubheading')}
                  </h2>
                  {searchResponse?.categories.map((category) => (
                    <Link
                      key={`category-${category.id}`}
                      href={`/${category.slug}`}
                      prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                      className="block text-primary-mid hover:text-primary-dark underline hover:no-underline my-[8px]"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              )}
              {!!searchResponse?.organizations.length && (
                <div className="mb-[24px]">
                  <h2
                    className={classNames(
                      'text-[12px] tracking-[3px] lg:tracking-[4.5px] font-medium leading-[24px] text-balance uppercase mb-[10px]',
                      rubik.className,
                    )}
                  >
                    {t('components.Search.organizationsSubheading')}
                  </h2>
                  {searchResponse?.organizations.map((organization) => (
                    <Link
                      key={`organization-${organization.id}`}
                      href={`/${organization.slug}`}
                      prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                      className="block text-primary-mid hover:text-primary-dark underline hover:no-underline my-[8px]"
                    >
                      {organization.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
